import { useState, type FormEvent } from "react";
import { formatMoney, formatPercent, formatQuantity, formatRate } from "../../api/money";
import {
  useDeleteLine,
  useEditLine,
  useStandardLines,
  useUpdateHeader,
  type LineInput,
  type QuotationDetail,
  type QuotationLine,
} from "../../api/quotations";
import NewLineRow from "./NewLineRow";
import ConfirmRemove from "../commercial/ConfirmRemove";

/** The supplier's own document: their reference and dates, the lines in their
 * words and figures, and the foot ladder their paper prints. Editable while
 * the quotation can still change; a changed price asks for its reason,
 * because the audit record needs it. */

export function QuoteHeaderForm({ detail }: { detail: QuotationDetail }) {
  const update = useUpdateHeader(detail.id);
  const [number, setNumber] = useState(detail.number);
  const [quoteDate, setQuoteDate] = useState(detail.quote_date ?? "");
  const [validUntil, setValidUntil] = useState(detail.valid_until ?? "");
  const [terms, setTerms] = useState(detail.terms ?? "");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    update.mutate({
      number: number.trim(),
      quote_date: quoteDate || null,
      valid_until: validUntil || null,
      terms: terms.trim() || null,
    });
  }

  return (
    <form className="cm-inline-box" onSubmit={onSubmit} aria-label="Quotation details">
      <label>
        Their reference
        <input value={number} onChange={(e) => setNumber(e.target.value)} required />
      </label>
      <label>
        Dated
        <input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} />
      </label>
      <label>
        Valid until
        <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
      </label>
      <label className="cm-grow">
        Their terms
        <input value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Validity, delivery, exclusions" />
      </label>
      <button type="submit" className="btn btn-secondary" disabled={update.isPending}>
        Save
      </button>
      {update.isError ? <p className="notice bad">{update.error.message}</p> : null}
    </form>
  );
}

const EMPTY: LineInput = { description: "", qty: "1", unit_price: "", discount_pct: "" };

function sizeAndPn(line: QuotationLine): string {
  return [line.size, line.pressure_nominal].filter(Boolean).join(" · ");
}

export function QuoteLines({ detail }: { detail: QuotationDetail }) {
  const editable = detail.actions.can_edit;
  const edit = useEditLine(detail.id);
  const remove = useDeleteLine(detail.id);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<LineInput>(EMPTY);
  const error = edit.error ?? remove.error;

  function startEdit(line: QuotationLine) {
    setEditing(line.id);
    setDraft({
      description: line.description,
      qty: line.qty,
      unit_price: line.unit_price,
      discount_pct: line.discount_pct,
      item_code: line.item_code ?? "",
      size: line.size ?? "",
      face_to_face: line.face_to_face ?? "",
      pressure_nominal: line.pressure_nominal ?? "",
      delivery: line.delivery ?? "",
      reason: "",
    });
  }

  return (
    <div className="table-card">
      <table className="data-table cm-lines" aria-label="Quotation lines">
        <thead>
          <tr>
            <th className="cm-seq">#</th>
            <th>Code</th>
            <th>Description</th>
            <th className="num">Qty</th>
            <th className="num">List price</th>
            <th className="num">Disc</th>
            <th className="num">Amount</th>
            {editable ? <th aria-label="Line tools" /> : null}
          </tr>
        </thead>
        <tbody>
          {detail.lines.length === 0 ? (
            <tr>
              <td colSpan={editable ? 8 : 7} className="muted">
                No lines yet.
              </td>
            </tr>
          ) : null}
          {detail.lines.map((line) =>
            editing === line.id ? (
              <tr key={line.id} className="cm-line-editing">
                <td className="cm-seq muted">{line.seq}</td>
                <td>
                  <input className="input" value={draft.item_code ?? ""} onChange={(e) => setDraft({ ...draft, item_code: e.target.value })} aria-label="Code" />
                </td>
                <td>
                  <input className="input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} aria-label="Description" />
                  <span className="cm-edit-row">
                    <input className="input" value={draft.size ?? ""} onChange={(e) => setDraft({ ...draft, size: e.target.value })} aria-label="Size" placeholder="Size" />
                    <input
                      className="input"
                      value={draft.pressure_nominal ?? ""}
                      onChange={(e) => setDraft({ ...draft, pressure_nominal: e.target.value })}
                      aria-label="PN"
                      placeholder="PN"
                    />
                  </span>
                  <input
                    className="input"
                    value={draft.reason ?? ""}
                    onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                    placeholder="Reason, if the price changed"
                    aria-label="Reason"
                  />
                </td>
                <td>
                  <input value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: e.target.value })} aria-label="Quantity" className="input num" />
                </td>
                <td>
                  <input value={draft.unit_price} onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })} aria-label="List price" className="input num" />
                </td>
                <td>
                  <input value={draft.discount_pct ?? ""} onChange={(e) => setDraft({ ...draft, discount_pct: e.target.value })} aria-label="Discount" className="input num" />
                </td>
                <td className="num muted">—</td>
                <td className="cm-tools">
                  <button
                    type="button"
                    className="row-button cm-save"
                    onClick={() => edit.mutate({ lineId: line.id, body: draft }, { onSuccess: () => setEditing(null) })}
                    disabled={edit.isPending}
                  >
                    Save
                  </button>
                  <button type="button" className="row-button" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={line.id}>
                <td className="cm-seq muted">{line.seq}</td>
                <td className="mono">{line.item_code ?? ""}</td>
                <td>
                  {line.description}
                  {sizeAndPn(line) ? <span className="sub">{sizeAndPn(line)}</span> : null}
                </td>
                <td className="num">{formatQuantity(line.qty)}</td>
                <td className="num">{formatMoney(line.unit_price)}</td>
                <td className="num muted">{formatPercent(line.discount_pct)}</td>
                <td className="num">{formatMoney(line.amount)}</td>
                {editable ? (
                  <td className="cm-tools">
                    <button type="button" className="row-button" onClick={() => startEdit(line)} aria-label={`Edit line ${line.seq}`}>
                      Edit
                    </button>
                    <ConfirmRemove what={`line ${line.seq}`} disabled={remove.isPending} onConfirm={() => remove.mutate(line.id)} />
                  </td>
                ) : null}
              </tr>
            ),
          )}
          {editable ? <NewLineRow detail={detail} columns={8} /> : null}
        </tbody>
      </table>
      {error ? <p className="notice bad">{error.message}</p> : null}
    </div>
  );
}

/** Labour and travelling at the supplier's own rates, added in one move while
 * the quotation is a draft. */
export function StandardLines({ detail }: { detail: QuotationDetail }) {
  const standard = useStandardLines(detail.id);
  const [hours, setHours] = useState("");
  const s = detail.standard_lines;
  if (!detail.actions.can_edit || detail.status !== "draft") {
    return null;
  }
  return (
    <div className="cm-inline-box">
      <label>
        Labour hours
        <input value={hours} onChange={(e) => setHours(e.target.value)} className="num" placeholder="3" disabled={s.has_labour} />
      </label>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={standard.isPending || (s.has_labour && s.has_travel)}
        onClick={() => standard.mutate(hours)}
      >
        Add labour and travelling
      </button>
      <p className="cm-doc-note">
        At {detail.supplier?.name ?? "the supplier"}'s rates: labour {formatMoney(s.labour_rate)}/h; travelling{" "}
        {formatMoney(s.km_rate)}/km over {s.km ? `${formatQuantity(s.km)} km` : "a journey not yet routed"}.
      </p>
      {standard.isError ? <p className="notice bad">{standard.error.message}</p> : null}
    </div>
  );
}

export function QuoteFoot({ detail }: { detail: QuotationDetail }) {
  const f = detail.foot;
  return (
    <table className="cm-foot">
      <tbody>
        <tr>
          <td>Sub total (list)</td>
          <td>{formatMoney(f.subtotal)}</td>
        </tr>
        <tr>
          <td>Discount</td>
          <td>{formatMoney(f.discount_amount)}</td>
        </tr>
        <tr>
          <td>Total excl VAT</td>
          <td>{formatMoney(f.total_excl)}</td>
        </tr>
        <tr>
          <td>VAT at {formatRate(f.vat_rate)}</td>
          <td>{formatMoney(f.vat)}</td>
        </tr>
        <tr className="total">
          <td>Total incl VAT</td>
          <td>{formatMoney(f.total_incl)}</td>
        </tr>
      </tbody>
    </table>
  );
}
